// src/components/gallery/CharacterGalleryGrid.tsx
/**
 * キャラクターギャラリーのグリッド表示コンポーネント
 * XOR-Pix画像をグリッドで表示し、クリックでモーダル表示
 * キャッシュ対応: orifoliCacheでフィードをキャッシュ
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PhotoIcon, PlusIcon, LightBulbIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { Loader2 } from 'lucide-react';
import { getCharacterGalleryImages, GalleryImage } from '../../services/characterService';
import XorPixViewer from '../xorpix/XorPixViewer';
import LikeButton from '../ui/LikeButton';
import { orifoliCache, CachedFeedEntry } from '../../services/orifoliCache';

interface CharacterGalleryGridProps {
  characterId: string;
  characterName?: string;
  isOwner: boolean;
  onImageClick?: (image: GalleryImage) => void;
  onUploadClick?: () => void;
}

// ギャラリーカードコンポーネント（画像 + アクションバー）
const GalleryCard: React.FC<{
  image: GalleryImage;
  onClick: () => void;
}> = ({ image, onClick }) => {
  // シェアURL生成
  const getShareUrl = (): string => {
    const isProduction = import.meta.env.VITE_API_BASE_URL === 'https://api.figryne.com';
    const bucketName = isProduction ? 'figryne-public-assets-prod' : 'figryne-public-assets';
    return isProduction
      ? `https://figryne.com/share/pix/${image.modelId}`
      : `https://storage.googleapis.com/${bucketName}/share/pix/${image.modelId}.html`;
  };

  // X (Twitter) シェア
  const handleShareX = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!image.isPublic && !image.isMembersOnly) return;
    const shareUrl = getShareUrl();
    const title = image.title || 'Untitled';
    const text = `${title} | ORIFOLI`;
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  // Bluesky シェア
  const handleShareBluesky = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!image.isPublic && !image.isMembersOnly) return;
    const shareUrl = getShareUrl();
    const title = image.title || 'Untitled';
    const text = `${title} | ORIFOLI`;
    window.open(
      `https://bsky.app/intent/compose?text=${encodeURIComponent(text + ' ' + shareUrl)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const isShareDisabled = !image.isPublic && !image.isMembersOnly;

  return (
    <div className="bg-gray-100 dark:bg-gray-800 rounded-xl overflow-hidden">
      {/* 画像エリア */}
      <div
        onClick={onClick}
        className="aspect-square overflow-hidden group cursor-pointer relative"
      >
        <XorPixViewer
          noise1Url={image.noise1Url}
          noise2Url={image.noise2Url}
          rid={image.rid}
          width={image.width}
          height={image.height}
          characterId={image.modelId}
          fallbackNoise1Url={image.fallbackNoise1Url}
          fallbackNoise2Url={image.fallbackNoise2Url}
          fitMode="cover"
          className="w-full h-full group-hover:scale-105 transition-transform duration-300"
        />
        {/* ホバーオーバーレイ */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors pointer-events-none" />
      </div>

      {/* アクションバー */}
      <div className="flex items-center justify-between px-3 py-2">
        {/* いいねボタン */}
        <LikeButton
          modelId={image.modelId}
          initialCount={image.likeCount || 0}
          className="flex items-center gap-1"
          iconClassName="w-4 h-4"
          textClassName="text-xs text-gray-500 dark:text-gray-400"
          disabled={isShareDisabled}
        />

        {/* シェアボタン */}
        <div className="flex items-center gap-2">
          {/* X シェアボタン */}
          <button
            onClick={handleShareX}
            disabled={isShareDisabled}
            className={`p-1 transition-colors ${
              isShareDisabled
                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
            }`}
            aria-label="Share on X"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
          </button>

          {/* Bluesky シェアボタン */}
          <button
            onClick={handleShareBluesky}
            disabled={isShareDisabled}
            className={`p-1 transition-colors ${
              isShareDisabled
                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                : 'text-gray-400 dark:text-gray-500 hover:text-[#0085ff]'
            }`}
            aria-label="Share on Bluesky"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
              <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

const CharacterGalleryGrid: React.FC<CharacterGalleryGridProps> = ({
  characterId,
  characterName,
  isOwner,
  onImageClick,
  onUploadClick,
}) => {
  const { t } = useTranslation('character');
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const imageMetadataRef = useRef<Map<string, GalleryImage>>(new Map());

  // キャッシュからフィードを復元
  const restoreFromCache = useCallback((): boolean => {
    const cachedData = orifoliCache.getFeedEntries('gallery', characterId);
    if (!cachedData || cachedData.entries.length === 0) {
      return false;
    }

    // キャッシュからメタデータを復元
    const cachedImages: GalleryImage[] = [];
    for (const entry of cachedData.entries) {
      const metadata = orifoliCache.getImageMetadata(entry.modelId) as GalleryImage | null;
      if (metadata) {
        cachedImages.push(metadata);
        imageMetadataRef.current.set(entry.modelId, metadata);
      }
    }

    if (cachedImages.length > 0) {
      setImages(cachedImages);
      setHasMore(cachedImages.length >= 20);
      console.log(`[CharacterGalleryGrid] Restored ${cachedImages.length} items from cache`);
      return true;
    }
    return false;
  }, [characterId]);

  // キャッシュに保存
  const saveToCache = useCallback((items: GalleryImage[]) => {
    if (items.length === 0) return;

    // フィードエントリを保存
    const entries: CachedFeedEntry[] = items.map(item => ({
      modelId: item.modelId,
      created: item.created,
    }));

    const firstTimestamp = items[0]?.created || null;
    const lastTimestamp = items[items.length - 1]?.created || null;

    orifoliCache.saveFeedEntries('gallery', characterId, entries, firstTimestamp, lastTimestamp);

    // メタデータを保存
    items.forEach(item => {
      orifoliCache.saveImageMetadata(item.modelId, item as unknown as Record<string, unknown>);
      imageMetadataRef.current.set(item.modelId, item);
    });
  }, [characterId]);

  // 初回読み込み
  const fetchImages = useCallback(async (forceRefresh = false) => {
    if (!characterId) return;

    // キャッシュから復元を試みる（強制リフレッシュでない場合）
    if (!forceRefresh && restoreFromCache()) {
      setIsLoading(false);
      // バックグラウンドで新しいアイテムをチェック
      const cachedData = orifoliCache.getFeedEntries('gallery', characterId);
      if (cachedData?.firstTimestamp) {
        try {
          const newItems = await getCharacterGalleryImages(characterId, 20);
          if (newItems.length > 0 && newItems[0].created !== cachedData.firstTimestamp) {
            // 新しいアイテムがある場合はマージ
            const existingIds = new Set(images.map(img => img.modelId));
            const uniqueNewItems = newItems.filter(item => !existingIds.has(item.modelId));
            if (uniqueNewItems.length > 0) {
              const mergedItems = [...uniqueNewItems, ...images];
              setImages(mergedItems);
              saveToCache(mergedItems);
            }
          }
        } catch {
          // バックグラウンド更新のエラーは無視
        }
      }
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const items = await getCharacterGalleryImages(characterId, 20);
      setImages(items);
      setHasMore(items.length === 20);
      saveToCache(items);
    } catch (err) {
      console.error('Error fetching gallery images:', err);
      setError(t('gallery.loadError'));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [characterId, t, restoreFromCache, saveToCache, images]);

  // 追加読み込み
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || images.length === 0) return;

    setIsLoadingMore(true);
    try {
      const lastImage = images[images.length - 1];
      const moreItems = await getCharacterGalleryImages(characterId, 20, lastImage.created);
      const newImages = [...images, ...moreItems];
      setImages(newImages);
      setHasMore(moreItems.length === 20);
      saveToCache(newImages);
    } catch (err) {
      console.error('Error loading more images:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [characterId, images, isLoadingMore, hasMore, saveToCache]);

  // リフレッシュ
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    orifoliCache.clearFeedCache('gallery', characterId);
    fetchImages(true);
  }, [characterId, fetchImages]);

  useEffect(() => {
    fetchImages();
  }, [characterId]); // fetchImagesを依存配列から除外して無限ループを防ぐ

  // ローディング状態
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-gray-400 dark:text-gray-500 animate-spin" />
      </div>
    );
  }

  // エラー状態
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400 mb-4">{error}</p>
        <button
          onClick={() => fetchImages(true)}
          className="text-purple-600 dark:text-purple-400 hover:underline text-sm"
        >
          {t('actions.retry')}
        </button>
      </div>
    );
  }

  // 空状態
  if (images.length === 0) {
    // オーナー向けの案内カード
    if (isOwner && onUploadClick) {
      return (
        <div className="py-6">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center shadow-sm border border-gray-100 dark:border-gray-700">
            <LightBulbIcon className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-700 dark:text-gray-300 text-sm mb-4">
              {t('gallery.emptyOwnerHint', { name: characterName || '' })}
            </p>
            <button
              onClick={onUploadClick}
              className="inline-flex items-center gap-2 px-5 py-2 mt-6 bg-purple-500 text-white rounded-full text-sm font-medium hover:bg-purple-600 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              {t('gallery.upload')}
            </button>
          </div>
        </div>
      );
    }

    // 一般ユーザー向けの空状態
    return (
      <div className="text-center py-12">
        <PhotoIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400">{t('gallery.empty')}</p>
      </div>
    );
  }

  return (
    <div>
      {/* リフレッシュボタン */}
      <div className="flex justify-end mb-3">
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="p-1.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
          title={t('actions.refresh')}
        >
          <ArrowPathIcon className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* グリッド */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {/* オーナー用アップロードボタン (最初の要素として) */}
        {isOwner && onUploadClick && (
          <div className="bg-gray-100 dark:bg-gray-800 rounded-xl overflow-hidden">
            <button
              onClick={onUploadClick}
              className="aspect-square w-full border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-purple-400 dark:hover:border-purple-500 transition-colors flex flex-col items-center justify-center gap-2 group"
            >
              <PlusIcon className="w-8 h-8 text-gray-400 dark:text-gray-500 group-hover:text-purple-500 transition-colors" />
              <span className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-purple-500 transition-colors">
                {t('gallery.add')}
              </span>
            </button>
            {/* カードと高さを揃えるためのスペーサー */}
            <div className="h-[40px]" />
          </div>
        )}

        {/* 画像一覧 */}
        {images.map((image) => (
          <GalleryCard
            key={image.modelId}
            image={image}
            onClick={() => onImageClick?.(image)}
          />
        ))}
      </div>

      {/* もっと読み込む */}
      {hasMore && (
        <div className="mt-6 text-center">
          <button
            onClick={loadMore}
            disabled={isLoadingMore}
            className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            {isLoadingMore ? (
              <Loader2 className="w-4 h-4 animate-spin inline-block" />
            ) : (
              t('actions.loadMore')
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default CharacterGalleryGrid;
